import type { ReactNode } from "react";
import Link from "next/link";
import Image, { type StaticImageData } from "next/image";

import { FONT_SIZES } from "@/components/ui/constants";
import { cn } from "@/lib/utils";

export type Props = {
	title: string;
	/** Image logo (e.g. Google). Omit when using `icon` (e.g. Apple). */
	logo?: StaticImageData | string;
	/** Optional inline icon instead of `logo` (Sign in with Apple, etc.) */
	icon?: ReactNode;
	href?: string;
	className?: string;
	iconClassName?: string;
	onClick?: () => void;
	disabled?: boolean;
};

export default function AuthProviderButton({
	logo,
	icon,
	title,
	onClick,
	href,
	className,
	iconClassName,
	disabled,
}: Props) {
	const baseClass = cn(
		`flex h-11 items-center ${FONT_SIZES.authButton} justify-center rounded-sm border border-secondary/10 bg-card px-4 py-0 text-card-foreground hover:bg-accent hover:text-accent-foreground dark:bg-card dark:text-card-foreground dark:border-border dark:hover:bg-accent dark:hover:text-accent-foreground`,
		"gap-x-2 xs:gap-x-3", // Reduced gap to match mobile app: 8px mobile, 12px xs
		"xs:px-10", // Responsive padding
		className,
	);

	const iconColumnClass =
		"flex h-6 w-6 shrink-0 items-center justify-center [&_.MuiSvgIcon-root]:block [&_.MuiSvgIcon-root]:leading-none";

	const children = (
		<>
			{icon != null ? (
				<div className={cn(iconColumnClass, iconClassName)}>{icon}</div>
			) : logo != null ? (
				<div className={cn(iconColumnClass, iconClassName)}>
					<Image
						draggable="false"
						src={logo}
						alt=""
						width={24}
						height={24}
						className="h-full w-full max-h-6 max-w-6 object-contain"
					/>
				</div>
			) : null}

			<span className="flex items-center text-nowrap font-normal leading-none">Continue with {title}</span>
		</>
	);

	if (href !== undefined) {
		return (
			<Link href={href} className={baseClass}>
				{children}
			</Link>
		);
	}

	return (
		<button 
			type="button" 
			onClick={onClick} 
			className={baseClass} 
			disabled={disabled}
		>
			{children}
		</button>
	);
}
